import { useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { setSelected } from "../../../redux/slice/selectionSlice";
import {
  useGetOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetBrokersQuery,
  useGetPlansQuery,
  useGetSubscriptionUpgradeRequestsQuery,
  useApproveSubscriptionUpgradeRequestMutation,
  useRejectSubscriptionUpgradeRequestMutation,
  useLazyGetSubscriptionsByOrganizationQuery,
  useCancelSubscriptionMutation,
} from "../../../redux/api";
import type { Organization, SubscriptionUpgradeRequest } from "../../../redux/api";
import { Modal } from "../../../components/Modal";
import { Field } from "../../../components/Field";
import { IconPlus, IconSearch, IconEdit, IconEye, IconSlash, IconTrash } from "../../../components/icons";
import { RowMenu } from "../../../components/RowMenu";
import { validateEmail } from "../../../utils/validation";
import { todayISO, addCycle, standardPriceFor } from "./subscription-helpers";

const COLUMN_COUNT = 7;

const formatRequestDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

type ApproveForm = {
  billingCycle: string;
  amountPaid: string;
  currency: string;
  autoRenew: string;
  startDate: string;
  endDate: string;
  paymentReference: string;
  remarks: string;
};

const emptyApproveForm: ApproveForm = {
  billingCycle: "monthly",
  amountPaid: "0",
  currency: "INR",
  autoRenew: "no",
  startDate: todayISO(),
  endDate: addCycle(todayISO(), "monthly"),
  paymentReference: "",
  remarks: "",
};

const STATUS_TONE: Record<string, string> = {
  active: "badge--success",
  trial: "badge--info",
  suspended: "badge--warning",
  expired: "badge--danger",
  deleted: "badge--danger",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const emptyForm = {
  name: "",
  ownerEmail: "",
  planId: "",
  status: "trial",
  industry: "",
  country: "India",
  currency: "INR",
  brokerId: "",
  tableLayoutLimit: "20",
};

// Platform Owner → Organizations. Lists every organization (services
// `organizations` query) and creates new ones via `createOrganization`.
const Organizations = () => {
  const dispatch = useAppDispatch();

  const {
    data: organizations = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetOrganizationsQuery();

  const [createOrganization, { isLoading: creating }] =
    useCreateOrganizationMutation();

  const [updateOrganization, { isLoading: savingLimit }] =
    useUpdateOrganizationMutation();

  const { data: brokers = [] } = useGetBrokersQuery();
  const { data: plans = [] } = useGetPlansQuery();
  const { data: pendingRequests = [] } = useGetSubscriptionUpgradeRequestsQuery("pending");

  const [approveRequest, { isLoading: approving }] = useApproveSubscriptionUpgradeRequestMutation();
  const [rejectRequest, { isLoading: rejecting }] = useRejectSubscriptionUpgradeRequestMutation();

  const [fetchOrgSubscriptions] = useLazyGetSubscriptionsByOrganizationQuery();
  const [cancelSubscription] = useCancelSubscriptionMutation();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCancelSubscription = async (org: Organization) => {
    if (!window.confirm(`Cancel ${org.name}'s active subscription? They'll lose access to paid features once it lapses.`)) {
      return;
    }
    setCancellingId(org.id);
    try {
      const subs = await fetchOrgSubscriptions(org.id).unwrap();
      const active = subs.find((s) => s.status === "active");
      if (!active) {
        window.alert(`${org.name} has no active subscription to cancel.`);
        return;
      }
      await cancelSubscription(active.id).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to cancel subscription.");
    } finally {
      setCancellingId(null);
    }
  };

  // Soft delete — organizations have no hard-delete mutation (by design: no
  // cascade-delete exists for their users/products/etc). This just flips
  // status to "deleted", same lifecycle field already used for
  // suspended/expired, and is fully reversible from this same menu.
  const handleDeleteOrganization = async (org: Organization) => {
    if (
      !window.confirm(
        `Delete ${org.name}? This marks the organization as deleted — its data isn't erased, and this can be undone by editing its status back.`,
      )
    ) {
      return;
    }
    setDeletingId(org.id);
    try {
      await updateOrganization({ id: org.id, input: { status: "deleted" } }).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to delete organization.");
    } finally {
      setDeletingId(null);
    }
  };

  const planName = (id?: string | null) => plans.find((p) => p.id === id)?.name ?? id ?? "—";
  const orgName = (id: string) => organizations.find((o) => o.id === id)?.name ?? id;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState<keyof Organization>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field required/format errors for the create-organization form —
  // shown directly under the offending input instead of the banner above.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  // Per-org "table layout limit" editor (platform owner only).
  const [limitOrg, setLimitOrg] = useState<Organization | null>(null);
  const [limitValue, setLimitValue] = useState("20");
  const [limitError, setLimitError] = useState<string | null>(null);
  const [limitFieldErrors, setLimitFieldErrors] = useState<Record<string, string | undefined>>({});

  const openLimitEditor = (org: Organization) => {
    setLimitOrg(org);
    setLimitValue(String(org.tableLayoutLimit ?? 20));
    setLimitError(null);
    setLimitFieldErrors({});
  };

  const openSubscriptions = (organizationId: string) => {
    dispatch(setSelected({ entity: "organization-subscriptions", id: organizationId }));
    dispatch(navigate("/organization-subscriptions"));
  };

  const [approvingRequest, setApprovingRequest] = useState<SubscriptionUpgradeRequest | null>(null);
  const [approveForm, setApproveForm] = useState<ApproveForm>(emptyApproveForm);
  const [approveError, setApproveError] = useState<string | null>(null);

  const openApprove = (request: SubscriptionUpgradeRequest) => {
    const plan = plans.find((p) => p.id === request.planId);
    // The owner's own proposed price (mainly set on Custom-plan requests,
    // which have no fixed price) takes priority over the plan's standard
    // price as a starting point — the platform owner can still change it
    // before approving.
    const price = request.proposedAmount ?? standardPriceFor(plan, "monthly", "INR");
    setApprovingRequest(request);
    setApproveError(null);
    setApproveForm({
      ...emptyApproveForm,
      amountPaid: price != null ? String(price) : "0",
      currency: request.proposedCurrency ?? emptyApproveForm.currency,
    });
  };

  const handleApproveCycleChange = (billingCycle: string) => {
    const plan = approvingRequest ? plans.find((p) => p.id === approvingRequest.planId) : undefined;
    const price = standardPriceFor(plan, billingCycle, approveForm.currency);
    setApproveForm((prev) => ({
      ...prev,
      billingCycle,
      amountPaid: price != null ? String(price) : prev.amountPaid,
      endDate: billingCycle === "custom" ? prev.endDate : addCycle(prev.startDate, billingCycle),
    }));
  };

  const handleApproveSubmit = async () => {
    if (!approvingRequest) return;
    setApproveError(null);
    try {
      await approveRequest({
        id: approvingRequest.id,
        input: {
          billingCycle: approveForm.billingCycle,
          amountPaid: Number(approveForm.amountPaid) || 0,
          currency: approveForm.currency,
          autoRenew: approveForm.autoRenew === "yes",
          startDate: approveForm.startDate,
          endDate: approveForm.endDate,
          paymentReference: approveForm.paymentReference.trim() || undefined,
          remarks: approveForm.remarks.trim() || undefined,
        },
      }).unwrap();
      setApprovingRequest(null);
    } catch (err) {
      setApproveError(
        (err as { message?: string })?.message ?? "Failed to approve request.",
      );
    }
  };

  const handleReject = async (request: SubscriptionUpgradeRequest) => {
    // One dialog, not two — cancelling it (returns null) means "don't
    // reject"; submitting it (even empty) confirms the rejection. The note
    // is shown back to the owner on their Subscription screen, e.g. to
    // counter-propose a different price for a Custom-plan request.
    const note = window.prompt(
      `Reject ${orgName(request.organizationId)}'s request to upgrade to ${planName(request.planId)}?\n\nAdd an optional note for the owner (e.g. a counter-offer price), or leave blank:`,
      "",
    );
    if (note === null) return;
    try {
      await rejectRequest({ id: request.id, note: note.trim() || undefined }).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to reject request.");
    }
  };

  const handleSaveLimit = async () => {
    if (!limitOrg) return;
    const value = Number(limitValue);
    if (!Number.isFinite(value) || value < 0) {
      setLimitFieldErrors({ limit: "Enter a valid non-negative number." });
      return;
    }
    setLimitFieldErrors({});
    try {
      await updateOrganization({
        id: limitOrg.id,
        input: { tableLayoutLimit: value },
      }).unwrap();
      setLimitOrg(null);
    } catch (err) {
      setLimitError(
        (err as { message?: string })?.message ?? "Failed to update limit.",
      );
    }
  };

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setFieldErrors({});
    setOpen(true);
  };

  const handleCreate = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) {
      errors.name = "Organization name is required.";
    }
    const emailError = validateEmail(form.ownerEmail);
    if (emailError) {
      errors.ownerEmail = emailError;
    }
    if (!form.planId) {
      errors.planId = "Please select a plan.";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    try {
      await createOrganization({
        ...form,
        name: form.name.trim(),
        ownerEmail: form.ownerEmail.trim(),
        tableLayoutLimit: Number(form.tableLayoutLimit) || 20,
      }).unwrap();
      setOpen(false);
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ??
          "Failed to create organization.",
      );
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return organizations.filter((o: Organization) => {
      if (statusFilter !== "all" && (o.status ?? "").toLowerCase() !== statusFilter) return false;
      if (!q) return true;
      return [o.name, o.ownerEmail, o.planId, o.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      );
    });
  }, [organizations, search, statusFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * dir;
      if (bv == null) return 1 * dir;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visible = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize],
  );

  const toggleSort = (key: keyof Organization) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortArrow = (key: keyof Organization) =>
    sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "";

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load organizations"
    : null;

  return (
    <div className="dashboard">
      {pendingRequests.length > 0 ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>Pending upgrade requests</h2>
              <p className="panel__sub">{pendingRequests.length} awaiting action</p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Plan requested</th>
                  <th>Proposed</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((r) => (
                  <tr key={r.id}>
                    <td className="cell-title">{orgName(r.organizationId)}</td>
                    <td>{planName(r.planId)}</td>
                    <td>
                      {typeof r.proposedAmount === "number" ? (
                        <>
                          {r.proposedCurrency ?? "INR"} {r.proposedAmount.toLocaleString()}
                          {r.requestNote ? (
                            <>
                              <br />
                              <span className="panel__sub">{r.requestNote}</span>
                            </>
                          ) : null}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="cell-date">{formatRequestDate(r.requestedAt)}</td>
                    <td>
                      <span className="row-actions">
                        <button
                          type="button"
                          className="btn btn--primary"
                          onClick={() => openApprove(r)}
                          disabled={approving || rejecting}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleReject(r)}
                          disabled={approving || rejecting}
                        >
                          Reject
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Organizations</h2>
            <p className="panel__sub">
              Showing {visible.length} of {sorted.length}
              {sorted.length !== organizations.length ? ` (${organizations.length} total)` : ""}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search organizations…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <select
              className="btn"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
              <option value="deleted">Deleted</option>
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <IconPlus /> New organization
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th className={sortKey === "name" ? "is-sorted" : undefined}>
                  <button type="button" className="th-sort" onClick={() => toggleSort("name")}>
                    Organization Name <span className="th-sort__arrow">{sortArrow("name")}</span>
                  </button>
                </th>
                <th className={sortKey === "ownerEmail" ? "is-sorted" : undefined}>
                  <button type="button" className="th-sort" onClick={() => toggleSort("ownerEmail")}>
                    Owner <span className="th-sort__arrow">{sortArrow("ownerEmail")}</span>
                  </button>
                </th>
                <th className={sortKey === "planId" ? "is-sorted" : undefined}>
                  <button type="button" className="th-sort" onClick={() => toggleSort("planId")}>
                    Plan <span className="th-sort__arrow">{sortArrow("planId")}</span>
                  </button>
                </th>
                <th className={sortKey === "status" ? "is-sorted" : undefined}>
                  <button type="button" className="th-sort" onClick={() => toggleSort("status")}>
                    Status <span className="th-sort__arrow">{sortArrow("status")}</span>
                  </button>
                </th>
                <th>Layouts</th>
                <th className={sortKey === "createdAt" ? "is-sorted" : undefined}>
                  <button type="button" className="th-sort" onClick={() => toggleSort("createdAt")}>
                    Created <span className="th-sort__arrow">{sortArrow("createdAt")}</span>
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading organizations…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {organizations.length === 0
                      ? "No organizations yet."
                      : "No organizations match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((org: Organization) => (
                  <tr key={org.id}>
                    <td className="cell-title">{org.name}</td>
                    <td className="cell-date">{org.ownerEmail}</td>
                    <td>
                      <span className="pill pill--neutral">{planName(org.planId)}</span>
                    </td>
                    <td>
                      <span className={`badge ${statusTone(org.status)}`}>
                        <span className="badge__dot" />
                        {org.status ?? "unknown"}
                      </span>
                    </td>
                    <td>
                      <span className="pill pill--neutral">
                        {org.tableLayoutUsage ?? 0} / {org.tableLayoutLimit ?? 20}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(org.createdAt)}</td>
                    <td>
                      {/* stopPropagation: the row itself is clickable, so a
                          menu click must not also open the organization. */}
                      <RowMenu
                        stopPropagation
                        items={[
                          {
                            label: "View subscriptions",
                            icon: <IconEye size={16} />,
                            onSelect: () => openSubscriptions(org.id),
                          },
                          {
                            label: "Edit layout limit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openLimitEditor(org),
                          },
                          {
                            label: cancellingId === org.id ? "Cancelling…" : "Cancel subscription",
                            icon: <IconSlash size={16} />,
                            onSelect: () => handleCancelSubscription(org),
                            disabled: cancellingId === org.id,
                          },
                          {
                            label: deletingId === org.id ? "Deleting…" : "Delete organization",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDeleteOrganization(org),
                            danger: true,
                            disabled: deletingId === org.id,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {sorted.length > 0 ? (
          <div className="table-footer">
            <label className="table-footer__pagesize">
              Page Size:
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <span className="table-footer__range">
              {(safePage - 1) * pageSize + 1} to {Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
            </span>
            <div className="table-footer__nav">
              <button type="button" className="btn btn--icon" disabled={safePage <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="btn btn--icon"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              <span className="table-footer__page">
                Page {safePage} of {pageCount}
              </span>
              <button
                type="button"
                className="btn btn--icon"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="btn btn--icon"
                disabled={safePage >= pageCount}
                onClick={() => setPage(pageCount)}
              >
                »
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <Modal
        open={open}
        title="Create organization"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setOpen(false)}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? "Creating…" : "Create organization"}
            </button>
          </>
        }
      >
        {formError ? <div className="form-error">{formError}</div> : null}
        <div className="form-grid">
          <Field label="Organization name *" full error={fieldErrors.name}>
            <input
              value={form.name}
              onChange={(e) => {
                setField("name", e.target.value);
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }}
              placeholder="Acme Inc."
            />
          </Field>
          <Field label="Owner email *" full error={fieldErrors.ownerEmail}>
            <input
              type="email"
              value={form.ownerEmail}
              onChange={(e) => {
                setField("ownerEmail", e.target.value);
                setFieldErrors((prev) => ({ ...prev, ownerEmail: undefined }));
              }}
              placeholder="owner@acme.com"
            />
          </Field>
          <Field label="Plan" error={fieldErrors.planId}>
            <select
              value={form.planId}
              onChange={(e) => {
                setField("planId", e.target.value);
                setFieldErrors((prev) => ({ ...prev, planId: undefined }));
              }}
            >
              <option value="">— Select a plan —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </Field>
          <label className="field">
            Status
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
            >
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
          </label>
          <label className="field">
            Industry
            <input
              value={form.industry}
              onChange={(e) => setField("industry", e.target.value)}
              placeholder="Software"
            />
          </label>
          <label className="field">
            Country
            <input
              value={form.country}
              onChange={(e) => setField("country", e.target.value)}
            />
          </label>
          <label className="field">
            Currency
            <input
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
            />
          </label>
          <label className="field">
            Table layout limit
            <input
              type="number"
              min={0}
              value={form.tableLayoutLimit}
              onChange={(e) => setField("tableLayoutLimit", e.target.value)}
              placeholder="20"
            />
          </label>
          <label className="field field--full">
            Referring broker
            <select
              value={form.brokerId}
              onChange={(e) => setField("brokerId", e.target.value)}
            >
              <option value="">— None —</option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.companyName ? ` (${b.companyName})` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Modal>

      <Modal
        open={Boolean(limitOrg)}
        title={`Table layout limit — ${limitOrg?.name ?? ""}`}
        onClose={() => setLimitOrg(null)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setLimitOrg(null)}
              disabled={savingLimit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSaveLimit}
              disabled={savingLimit}
            >
              {savingLimit ? "Saving…" : "Save limit"}
            </button>
          </>
        }
      >
        {limitError ? <div className="form-error">{limitError}</div> : null}
        <p className="panel__sub" style={{ marginBottom: 12 }}>
          Maximum number of <strong>shared</strong> table layouts (organization &
          specific-user) this organization may save. Personal layouts are
          unlimited. Currently used: {limitOrg?.tableLayoutUsage ?? 0}.
        </p>
        <div className="form-grid">
          <Field label="Layout limit" full error={limitFieldErrors.limit}>
            <input
              type="number"
              min={0}
              value={limitValue}
              onChange={(e) => {
                setLimitValue(e.target.value);
                setLimitFieldErrors((prev) => ({ ...prev, limit: undefined }));
              }}
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!approvingRequest}
        title={
          approvingRequest
            ? `Approve upgrade — ${orgName(approvingRequest.organizationId)} → ${planName(approvingRequest.planId)}`
            : ""
        }
        onClose={() => setApprovingRequest(null)}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={() => setApprovingRequest(null)}
              disabled={approving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleApproveSubmit}
              disabled={approving}
            >
              {approving ? "Approving…" : "Approve & activate"}
            </button>
          </>
        }
      >
        {approveError ? <div className="form-error">{approveError}</div> : null}
        {approvingRequest && typeof approvingRequest.proposedAmount === "number" ? (
          <p className="panel__sub" style={{ marginTop: 0 }}>
            Owner proposed: {approvingRequest.proposedCurrency ?? "INR"}{" "}
            {approvingRequest.proposedAmount.toLocaleString()}
            {approvingRequest.requestNote ? ` — ${approvingRequest.requestNote}` : ""}
          </p>
        ) : null}
        <div className="form-grid">
          <label className="field">
            Currency
            <select
              value={approveForm.currency}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, currency: e.target.value }))
              }
            >
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label className="field">
            Billing cycle
            <select
              value={approveForm.billingCycle}
              onChange={(e) => handleApproveCycleChange(e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly (3 months)</option>
              <option value="half-yearly">Half-yearly (6 months)</option>
              <option value="yearly">Yearly</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          <label className="field">
            Amount to charge
            <input
              type="number"
              value={approveForm.amountPaid}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, amountPaid: e.target.value }))
              }
            />
          </label>
          <label className="field">
            Start date
            <input
              type="date"
              value={approveForm.startDate}
              onChange={(e) =>
                setApproveForm((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                  endDate:
                    prev.billingCycle === "custom"
                      ? prev.endDate
                      : addCycle(e.target.value, prev.billingCycle),
                }))
              }
            />
          </label>
          <label className="field">
            End date
            <input
              type="date"
              value={approveForm.endDate}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </label>
          <label className="field">
            Auto-renew
            <select
              value={approveForm.autoRenew}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, autoRenew: e.target.value }))
              }
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          <label className="field">
            Payment reference
            <input
              value={approveForm.paymentReference}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, paymentReference: e.target.value }))
              }
            />
          </label>
          <label className="field field--full">
            Remarks
            <textarea
              value={approveForm.remarks}
              onChange={(e) =>
                setApproveForm((prev) => ({ ...prev, remarks: e.target.value }))
              }
            />
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default Organizations;
